import type { KnowledgeDocument } from '@hostesa/api-client';
import {
  useApproveKnowledgeDocument,
  useArchiveKnowledgeDocument,
  useDeactivateKnowledgeDocument,
  useReactivateKnowledgeDocument,
} from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@hostesa/ui';
import { useState } from 'react';
import { namespace } from './i18n';
import { asText, documentStatusVariant } from './lib';

/**
 * DocumentList — renders the resolved knowledge document (loaded by id, since
 * the catalogue exposes a document DETAIL endpoint, not a list) plus its
 * lifecycle actions. Approve is gated by `knowledge:approve` at the button
 * level; the route already gates `knowledge:read`.
 */
export function DocumentList({
  tenantId,
  hotelId,
  document,
}: {
  tenantId: string;
  hotelId: string;
  document: KnowledgeDocument;
}) {
  const { t } = useT(namespace);
  const { can } = useAuth();
  const canApprove = can('knowledge:approve');

  const approve = useApproveKnowledgeDocument(tenantId, hotelId);
  const deactivate = useDeactivateKnowledgeDocument(tenantId, hotelId);
  const reactivate = useReactivateKnowledgeDocument(tenantId, hotelId);
  const archive = useArchiveKnowledgeDocument(tenantId, hotelId);
  const [pending, setPending] = useState<string | null>(null);

  const sourceUrl = asText(document.sourceUrl);
  const busy = pending !== null;

  function run(action: 'approve' | 'deactivate' | 'reactivate' | 'archive') {
    setPending(action);
    const vars = { documentId: document.id };
    const onDone = () => setPending(null);
    if (action === 'approve') approve.mutate(vars, { onSettled: onDone });
    else if (action === 'deactivate') deactivate.mutate(vars, { onSettled: onDone });
    else if (action === 'reactivate') reactivate.mutate(vars, { onSettled: onDone });
    else archive.mutate(vars, { onSettled: onDone });
  }

  return (
    <Table data-testid="knowledge-document-table">
      <TableHeader>
        <TableRow>
          <TableHead>{t('documents.columnTitle')}</TableHead>
          <TableHead>{t('documents.columnCategory')}</TableHead>
          <TableHead>{t('documents.columnLanguage')}</TableHead>
          <TableHead>{t('documents.columnSource')}</TableHead>
          <TableHead>{t('documents.columnStatus')}</TableHead>
          <TableHead>{t('documents.columnUpdated')}</TableHead>
          <TableHead aria-hidden />
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow data-testid={`knowledge-document-row-${document.id}`}>
          <TableCell>
            <span className="font-medium text-[var(--color-charcoal)]">{document.title}</span>
          </TableCell>
          <TableCell>{t(`category.${document.category}`)}</TableCell>
          <TableCell>{document.language}</TableCell>
          <TableCell>
            {sourceUrl ? (
              <a
                data-testid="knowledge-document-source-link"
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-[var(--color-charcoal)] underline"
              >
                {t('documents.sourceLink')}
              </a>
            ) : (
              <span className="text-sm text-[var(--color-stone-mid)]">{document.sourceType}</span>
            )}
          </TableCell>
          <TableCell>
            <StatusBadge
              data-testid="knowledge-document-status"
              status={documentStatusVariant(document.status)}
              label={t(`status.${document.status}`)}
            />
          </TableCell>
          <TableCell className="text-sm text-[var(--color-stone-mid)]">
            {document.updatedAt}
          </TableCell>
          <TableCell>
            <div className="flex flex-wrap justify-end gap-2">
              {document.status !== 'approved' && canApprove && (
                <Button
                  size="sm"
                  variant="primary"
                  data-testid="knowledge-approve-btn"
                  disabled={busy}
                  onClick={() => run('approve')}
                >
                  {pending === 'approve' ? t('actions.approving') : t('actions.approve')}
                </Button>
              )}
              {document.active ? (
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid="knowledge-deactivate-btn"
                  disabled={busy}
                  onClick={() => run('deactivate')}
                >
                  {t('actions.deactivate')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid="knowledge-reactivate-btn"
                  disabled={busy}
                  onClick={() => run('reactivate')}
                >
                  {t('actions.reactivate')}
                </Button>
              )}
              {document.status !== 'archived' && (
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="knowledge-archive-btn"
                  disabled={busy}
                  onClick={() => run('archive')}
                >
                  {t('actions.archive')}
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
